// src/pages/InviteUser/index.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Input,
  Button,
  Alert,
  Spinner,
} from "reactstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { sendUserInvite } from "../../helpers/eirna_backend_helper";

const InviteUser = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Validation schema
  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Please enter a valid email address")
      .required("Email address is required"),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setIsLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      await sendUserInvite({ email: values.email });
      setSuccessMessage(`Invitation sent successfully to ${values.email}`);
      resetForm();
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
          "Failed to send invitation. Please try again."
      );
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  document.title = "Invite User | EIRNABio Connect";

  return (
    <div className="page-content">
      <Container fluid>
        {/* Page Header */}
        <Row className="mb-3">
          <Col lg={12}>
            <h4 className="mb-0 fw-bold" style={{ color: "#374151" }}>
              Invite User
            </h4>
          </Col>
        </Row>

        {/* Main Content */}
        <Row>
          <Col>
            <Card
              style={{
                backgroundColor: "#ECF4FE",
                border: "1px solid #e5e7eb",
                borderRadius: "2px",
                boxShadow: "0 1px 3px rgba(179, 178, 178, 0.1)",
              }}
            >
              <CardBody style={{ padding: "32px" }}>
                {/* Header Section */}
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-3">
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: "#f3f4f6",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "12px",
                      }}
                    >
                      <i
                        className="ri-user-add-line"
                        style={{
                          fontSize: "20px",
                          color: "#6b7280",
                        }}
                      ></i>
                    </div>
                    <h5
                      className="mb-0 fw-semibold"
                      style={{
                        color: "#1f2937",
                        fontSize: "18px",
                      }}
                    >
                      Send Invite
                    </h5>
                  </div>
                </div>

                {/* Alerts */}
                {successMessage && (
                  <Alert
                    color="success"
                    className="mb-3"
                    style={{
                      backgroundColor: "#f0fdf4",
                      borderColor: "#bbf7d0",
                      color: "#166534",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  >
                    <i className="ri-check-circle-line me-2"></i>
                    {successMessage}
                  </Alert>
                )}

                {errorMessage && (
                  <Alert
                    color="danger"
                    className="mb-3"
                    style={{
                      backgroundColor: "#fef2f2",
                      borderColor: "#fecaca",
                      color: "#dc2626",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  >
                    <i className="ri-error-warning-line me-2"></i>
                    {errorMessage}
                  </Alert>
                )}

                {/* Form */}
                <Formik
                  initialValues={{ email: "" }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <FormGroup className="mb-3">
                        <Input
                          type="email"
                          name="email"
                          placeholder="Enter email address"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          invalid={errors.email && touched.email}
                          style={{
                            height: "48px",
                            fontSize: "15px",
                            borderRadius: "6px",
                            border: "1px solid #d1d5db",
                            backgroundColor: "#f9fafb",
                            color: "#374151",
                            padding: "0 16px",
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = "#2F9E37";
                            e.target.style.backgroundColor = "#ffffff";
                            e.target.style.boxShadow =
                              "0 0 0 3px rgba(47, 158, 55, 0.1)";
                          }}
                        />
                        {errors.email && touched.email && (
                          <div
                            style={{
                              color: "#dc2626",
                              fontSize: "13px",
                              marginTop: "6px",
                            }}
                          >
                            <i className="ri-error-warning-line me-1"></i>
                            {errors.email}
                          </div>
                        )}
                      </FormGroup>

                      {/* Note */}
                      <div
                        className="mb-4"
                        style={{
                          padding: "12px 16px",
                          backgroundColor: "#eff6ff",
                          borderRadius: "6px",
                          border: "1px solid #dbeafe",
                        }}
                      >
                        <p
                          className="mb-0"
                          style={{
                            fontSize: "13px",
                            color: "#1e40af",
                            lineHeight: "1.4",
                          }}
                        >
                          <strong>Note:</strong> Invited users will be able to
                          access all of your data.
                        </p>
                      </div>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={isSubmitting || isLoading}
                        style={{
                          height: "40px",
                          backgroundColor: "#2F9E37",
                          borderColor: "#2F9E37",
                          borderRadius: "20px",
                          fontWeight: "500",
                          fontSize: "14px",
                          padding: "0 24px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: "140px",
                        }}
                        onMouseEnter={(e) => {
                          if (!isLoading && !isSubmitting) {
                            e.target.style.backgroundColor = "#268a2f";
                            e.target.style.borderColor = "#268a2f";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isLoading && !isSubmitting) {
                            e.target.style.backgroundColor = "#2F9E37";
                            e.target.style.borderColor = "#2F9E37";
                          }
                        }}
                      >
                        {isLoading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Sending...
                          </>
                        ) : (
                          "Send Invite"
                        )}
                      </Button>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default InviteUser;
